import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  AlertModule,
  DropdownsModule,
  ModalModule,
  PopoverModule
} from '@ipreo/ngx-sprinkles';
import { LicenseManager } from 'ag-grid-enterprise';
import { BriefAgencyRatingComponent } from './components/agency-rating/brief-agency-rating/brief-agency-rating.component';
import { FullAgencyRatingComponent } from './components/agency-rating/full-agency-rating/full-agency-rating.component';
import { AppliedFiltersComponent } from './components/applied-filters/applied-filters.component';
import { AppliedFiltersPopoverComponent } from './components/appliedFiltersPopover/applied-filters-popover.component';
import { AssignmentsGroupDeleteModalComponent } from './components/assignments-group-delete-modal/assignments-group-delete-modal.component';
import { AssignmentsGroupListComponent } from './components/assignments-group-list/assignments-group-list.component';
import { AssignmentsRoleListComponent } from './components/assignments-role-list/assignments-role-list.component';
import { BankLogoComponent } from './components/bank-logo/bank-logo.component';
import { BidResultsModalComponent } from './components/bid-results/bid-results-modal.component';
import { BidResultsComponent } from './components/bid-results/bid-results.component';
import { CheckboxOptionComponent } from './components/checkbox-option/checkbox-option.component';
import { ComboIconComponent } from './components/combo-icon/combo-icon.component';
import { EnhancementComponent } from './components/enhancement/enhancement.component';
import { SeriesEnhancementComponent } from './components/enhancement/series/series-enhancement.component';
import { ExportModalComponent } from './components/export-modal/export-modal.component';
import { ExportWithModalComponent } from './components/export-with-modal/export-with-modal.component';
import { OrderPeriodComponent } from './components/order-period/order-period.component';
import { WeekSelectorComponent } from './components/week-selector/week-selector.component';
import { AnalyticsFacade } from './facades/analytics.facade';
import { AssignmentGroupsFacade } from './facades/assignment-groups.facade';
import { BenchmarkCurvesFacade } from './facades/benchmark-curve.facade';
import { CasaSettingsFacade } from './facades/casa-settings.facade';
import { DealFacade } from './facades/deal.facade';
import { ExportFacade } from './facades/export.facade';
import { FollowsFacade } from './facades/follows.facade';
import { PermissionsFacade } from './facades/permissions.facade';
import { PreferencesFacade } from './facades/preferences.facade';
import { SessionFacade } from './facades/session.facade';
import { UsersFacade } from './facades/users.facade';
import { CombinedDocumentCellComponent } from './grid/components/cells/combined-document.component';
import { DateCellComponent } from './grid/components/cells/date.component';
import { DealSelectorComponent } from './grid/components/cells/deal-selector.component';
import { DealStatusCellComponent } from './grid/components/cells/deal-status.component';
import { ESGCellComponent } from './grid/components/cells/esg.component';
import { FollowCellComponent } from './grid/components/cells/follow.component';
import { ParCellComponent } from './grid/components/cells/par/par.component';
import { CellPopoverComponent } from './grid/components/cells/popover/cell-popover.component';
import { RatingsCellComponent } from './grid/components/cells/ratings.component';
import { SaleDateCellComponent } from './grid/components/cells/sale-date.component';
import { ScoreCellComponent } from './grid/components/cells/score.component';
import { TimeCellComponent } from './grid/components/cells/time.component';
import { CombinedDocumentFilterComponent } from './grid/components/filters/combined-document-filter/combined-document-filter.component';
import { DatedDateFilterComponent } from './grid/components/filters/dated-date-filter/dated-date-filter.component';
import { DropdownFloatingFilterComponent } from './grid/components/filters/dropdown-floating-filter.component';
import { ESGFilterComponent } from './grid/components/filters/esg-filter/esg-filter.component';
import { FirstCallFilterComponent } from './grid/components/filters/first-call-filter/first-call-filter.component';
import { ParFilterComponent } from './grid/components/filters/par-filter/par-filter.component';
import { RadioSetFilterComponent } from './grid/components/filters/radio-set-filter/radio-set-filter.component';
import { RatingEnhancementFilterComponent } from './grid/components/filters/rating-enhancement-filter/rating-enhancement-filter.component';
import { StructureFilterComponent } from './grid/components/filters/structure-filter/structure-filter.component';
import { WeekOfFilterComponent } from './grid/components/filters/week-of-filter/week-of-filter.component';
import { PopupDraggableDirective } from './helpers/directives/popup-draggable.directive';
import { SetFilterService } from './helpers/filters/set-filter.service';
import { CurrencyExtendedPipe } from './pipes/currency-extended.pipe';
import { FilterLabelPipe } from './pipes/filter-label.pipe';
import { FilterValuePipe } from './pipes/filter-value.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { IllionsPipe } from './pipes/illions.pipe';
import { MomentizerPipe } from './pipes/momentizer.pipe';
import { NullToDashesPipe } from './pipes/null-to-dashes.pipe';
import { OrderPeriodCountdownPipe } from './pipes/order-period-countdown.pipe';
import { OrderPeriodHoverPipe } from './pipes/order-period-hover.pipe';
import { OrderPeriodTypePipe } from './pipes/order-period-type.pipe';
import { SortByPipe } from './pipes/sort-by.pipe';
import { AnalyticsService } from './services/analytics.service';
import { AssignmentGroupsService } from './services/assignment-groups.service';
import { AssignmentsService } from './services/assignments.service';
import { BenchmarkService } from './services/benchmark.service';
import { BondLinkService } from './services/bond-link.service';
import { CalendarService } from './services/calendar.service';
import { CasaSettingsService } from './services/casa-settings.service';
import { CustomContextMenuService } from './services/custom-context-menu.service';
import { BidResultsService } from './services/deal-bid-results.service';
import { DealDocumentsService } from './services/deal-documents.service';
import { DealMetricsService } from './services/deal-metrics.service';
import { DealService } from './services/deal.service';
import { ExportExcelService } from './services/export-excel.service';
import { ExportModalService } from './services/export-modal.service';
import { FiltersFacade } from './services/filters.facade';
import { FollowsService } from './services/follows.service';
import { Globals } from './services/globals';
import { OrderPeriodCountdownService } from './services/issue/order-period-countdown.service';
import { LiveUpdateService } from './services/live-update.service';
import { ManagerService } from './services/manager.service';
import { MonitorService } from './services/monitor.service';
import { NotifyService } from './services/notify.service';
import { OrderService } from './services/order.service';
import { SessionService } from './services/session.service';
import { TreasuriesService } from './services/treasuries.service';
import { URLService } from './services/url.service';
import { UserPermissionsService } from './services/user-permissions.service';
import { UserPreferencesService } from './services/user-preferences.service';
import { UsersService } from './services/users.service';
import { WeekSelectorService } from './services/week-selector.service';
import { WiresService } from './services/wires.service';
// eslint-disable-next-line max-len
import { IssueTypeOptionsFilterRendererComponent } from './grid/components/filters/cell-renderers/issue-type-options-cell-renderer.component';
import { 
  OfferingTypeFilterRendererComponent
} from './grid/components/filters/cell-renderers/offering-type-filter-cell-renderer.component';
import { SectorOptionsFilterRendererComponent } from './grid/components/filters/cell-renderers/sector-options-cell-renderer.component';
import { StateOptionsFilterRendererComponent } from './grid/components/filters/cell-renderers/state-options-cell-renderer.component';
import { TaxStatusFilterRendererComponent } from './grid/components/filters/cell-renderers/tax-status-filter-cell-renderer.component';
import { TypeFilterRendererComponent } from './grid/components/filters/cell-renderers/type-filter-cell-renderer.component';
// eslint-disable-next-line max-len
import { BreakdownFormConfirmModalComponent } from './components/breakdown-form-confirm-modal/breakdown-form-confirm-modal.component';
import { 
  BreakdownSubmissionErrorModalComponent
 } from './components/breakdown-submission-error-modal/breakdown-submission-error-modal.component';
import { MiniCardHeaderComponent } from './components/mini-card-header/mini-card-header.component';
import { ParPopoverComponent } from './components/par-popover/par-popover.component';
import { 
  SubmissionConflictErrorModalComponent
 } from './components/submission-conflict-error-modal/submission-conflict-error-modal.component';
import { DynamicLinkRendererComponent } from './grid/components/cells/dynamic-link-renderer.component';
import { 
  PricingStatusFilterRendererComponent
 } from './grid/components/filters/cell-renderers/pricing-status-filter-cell-renderer.component';
import { OfferingTypeFilterComponent } from './grid/components/filters/offering-type-filter/offering-type-filter.component';
import { IconHeaderComponent } from './grid/components/headers/icon-header/icon-header.component';
import { DesignationService } from './services/designations.service';

LicenseManager.setLicenseKey(
  // eslint-disable-next-line max-len
  '--REMOVED--'
);

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxSliderModule,
    ModalModule,
    PopoverModule,
    DropdownsModule,
    AlertModule
  ],
  declarations: [
    MiniCardHeaderComponent,
    DropdownFloatingFilterComponent,
    RatingEnhancementFilterComponent,
    ESGFilterComponent,
    DatedDateFilterComponent,
    FirstCallFilterComponent,
    CombinedDocumentFilterComponent,
    FollowCellComponent,
    RatingsCellComponent,
    DateCellComponent,
    SaleDateCellComponent,
    TimeCellComponent,
    ESGCellComponent,
    CombinedDocumentCellComponent,
    ScoreCellComponent,
    OrderPeriodComponent,
    BankLogoComponent,
    ComboIconComponent,
    EnhancementComponent,
    SeriesEnhancementComponent,
    WeekSelectorComponent,
    AppliedFiltersComponent,
    IllionsPipe,
    FilterPipe,
    NullToDashesPipe,
    MomentizerPipe,
    OrderPeriodTypePipe,
    OrderPeriodHoverPipe,
    OrderPeriodCountdownPipe,
    FilterLabelPipe,
    FilterValuePipe,
    SortByPipe,
    CurrencyExtendedPipe,
    AssignmentsRoleListComponent,
    AssignmentsGroupListComponent,
    ExportModalComponent,
    AppliedFiltersPopoverComponent,
    ExportWithModalComponent,
    AssignmentsGroupDeleteModalComponent,
    CheckboxOptionComponent,
    BriefAgencyRatingComponent,
    FullAgencyRatingComponent,
    DealStatusCellComponent,
    DealSelectorComponent,
    WeekOfFilterComponent,
    ParFilterComponent,
    ParCellComponent,
    StructureFilterComponent,
    CellPopoverComponent,
    BidResultsComponent,
    BidResultsModalComponent,
    RadioSetFilterComponent,
    IssueTypeOptionsFilterRendererComponent,
    TypeFilterRendererComponent,
    OfferingTypeFilterRendererComponent,
    TaxStatusFilterRendererComponent,
    StateOptionsFilterRendererComponent,
    SectorOptionsFilterRendererComponent,
    PricingStatusFilterRendererComponent,
    OfferingTypeFilterComponent,
    IconHeaderComponent,
    ParPopoverComponent,
    PopupDraggableDirective,
    DynamicLinkRendererComponent,
    BreakdownSubmissionErrorModalComponent,
    SubmissionConflictErrorModalComponent,
    BreakdownFormConfirmModalComponent
  ],
  providers: [
    AnalyticsService,
    DealService,
    CalendarService,
    BenchmarkService,
    MonitorService,
    ManagerService,
    FollowsService,
    WiresService,
    WeekSelectorService,
    NotifyService,
    UserPreferencesService,
    UserPermissionsService,
    UsersService,
    LiveUpdateService,
    OrderPeriodCountdownService,
    SessionService,
    BondLinkService,
    IllionsPipe,
    FilterPipe,
    Globals,
    AssignmentsService,
    FiltersFacade,
    AssignmentGroupsService,
    CasaSettingsService,
    OrderService,
    AssignmentGroupsFacade,
    BenchmarkCurvesFacade,
    AnalyticsFacade,
    DealFacade,
    FollowsFacade,
    SessionFacade,
    CasaSettingsFacade,
    PreferencesFacade,
    PermissionsFacade,
    UsersFacade,
    DealMetricsService,
    TreasuriesService,
    ExportModalService,
    ExportExcelService,
    ExportFacade,
    SetFilterService,
    BidResultsService,
    DealDocumentsService,
    DesignationService,
    CustomContextMenuService,
    URLService
  ],
  exports: [
    MiniCardHeaderComponent,
    FullAgencyRatingComponent,
    BankLogoComponent,
    ComboIconComponent,
    EnhancementComponent,
    SeriesEnhancementComponent,
    WeekSelectorComponent,
    AppliedFiltersComponent,
    IllionsPipe,
    FilterPipe,
    CurrencyExtendedPipe,
    NullToDashesPipe,
    MomentizerPipe,
    OrderPeriodComponent,
    OrderPeriodTypePipe,
    OrderPeriodHoverPipe,
    OrderPeriodCountdownPipe,
    FilterLabelPipe,
    FilterValuePipe,
    SortByPipe,
    AssignmentsRoleListComponent,
    AssignmentsGroupListComponent,
    ExportModalComponent,
    ExportWithModalComponent,
    CheckboxOptionComponent,
    StructureFilterComponent,
    BidResultsComponent,
    BidResultsModalComponent,
    RadioSetFilterComponent,
    IssueTypeOptionsFilterRendererComponent,
    TypeFilterRendererComponent,
    OfferingTypeFilterRendererComponent,
    TaxStatusFilterRendererComponent,
    StateOptionsFilterRendererComponent,
    SectorOptionsFilterRendererComponent,
    OfferingTypeFilterComponent,
    PricingStatusFilterRendererComponent,
    IconHeaderComponent,
    BreakdownFormConfirmModalComponent
  ],
  entryComponents: [
    RatingsCellComponent,
    DateCellComponent,
    SaleDateCellComponent,
    TimeCellComponent,
    ESGCellComponent,
    CombinedDocumentCellComponent,
    ScoreCellComponent,
    RatingEnhancementFilterComponent,
    DatedDateFilterComponent,
    FirstCallFilterComponent,
    ESGFilterComponent,
    CombinedDocumentFilterComponent,
    FollowCellComponent,
    OrderPeriodComponent,
    AppliedFiltersComponent,
    AssignmentsRoleListComponent,
    AssignmentsGroupListComponent,
    ExportModalComponent,
    AppliedFiltersPopoverComponent,
    AssignmentsGroupDeleteModalComponent,
    DealStatusCellComponent,
    DealSelectorComponent,
    WeekOfFilterComponent,
    ParFilterComponent,
    ParCellComponent,
    StructureFilterComponent,
    RadioSetFilterComponent,
    CellPopoverComponent,
    BidResultsComponent,
    BidResultsModalComponent,
    IssueTypeOptionsFilterRendererComponent,
    TypeFilterRendererComponent,
    OfferingTypeFilterRendererComponent,
    TaxStatusFilterRendererComponent,
    StateOptionsFilterRendererComponent,
    SectorOptionsFilterRendererComponent,
    PricingStatusFilterRendererComponent,
    IconHeaderComponent,
    ParPopoverComponent,
    DynamicLinkRendererComponent
  ]
})
export class SharedModule {
  // constructor(
  //   store: Store<SharedState>,
  //   @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  // ) {
  //   this.onAppLoad(store, config);
  // }
  // static forChild(): ModuleWithProviders {
  //   return {
  //     ngModule: SharedModule,
  //     providers: [FiltersFacade]
  //   };
  // }
  // static forRoot(): ModuleWithProviders<SharedModule> {
  //   return {
  //     ngModule: SharedModule
  //   };
  // }
  // private onAppLoad(
  //   store: Store<SharedState>,
  //   config: EnvironmentConfig
  // ): void {
  //   // if (config.params.platform === 'buyside') {
  //   //   store.dispatch(new LoadUsers());
  //   // }
  // }
}
